/**
 * The route table the SPA and the API share (planning-app.md §3 *Navigation from v3.8*, §4.3;
 * planning-app-information-architecture.md §1, PA62): nine flat nav items in Jonathan's order, the
 * four of them the phone bar shows (the rest sit behind *More*), which nav item owns each object
 * URL, and the redirect table every old room path answers with — one source for `<Navigate replace>`
 * in the SPA and the `301` the API sends on an old HTML path. An object's URL is its identity and
 * never changes; a room's URL follows the nav. Since `v3.8c` the nine items are the whole nav: the
 * Decision room, the sessions and the Verification desk live on as redirects only (IA §1.2).
 */
export type NavId = 'pulse' | 'plan' | 'tracks' | 'stories' | 'wireframes' | 'docs' | 'workspace' | 'build' | 'you';

export interface NavItem {
  id: NavId;
  path: string;
  /** English fallback; the SPA renders `routes[path]` from its catalog. */
  title: string;
  /** `tab`: one of the four phone tabs; `more`: listed in the phone's *More* sheet. */
  phone: 'tab' | 'more';
  /** The item's landing page is desktop work (the phone reads, comments and hands off). */
  desktopOnly: boolean;
  /** The package that ships the item; an unshipped item's route renders a placeholder naming it. */
  package: string;
  shipped: boolean;
}

export const NAV: readonly NavItem[] = [
  { id: 'pulse', path: '/', title: 'Pulse', phone: 'tab', desktopOnly: false, package: 'v1.6 pulse', shipped: true },
  { id: 'plan', path: '/plan', title: 'Plan', phone: 'more', desktopOnly: true, package: 'v2.1 plan board', shipped: true },
  { id: 'tracks', path: '/tracks', title: 'Tracks', phone: 'tab', desktopOnly: false, package: 'v3.8a flat nav + routes', shipped: true },
  { id: 'stories', path: '/stories', title: 'User Stories', phone: 'more', desktopOnly: false, package: 'v3.9a user stories', shipped: true },
  { id: 'wireframes', path: '/wireframes', title: 'Wireframes', phone: 'more', desktopOnly: false, package: 'v2.9 wireframe studio', shipped: true },
  { id: 'docs', path: '/docs', title: 'Documents', phone: 'more', desktopOnly: false, package: 'v2.7 translation', shipped: true },
  { id: 'workspace', path: '/threads', title: 'Workspace', phone: 'tab', desktopOnly: false, package: 'v1.7 workspace core', shipped: true },
  { id: 'build', path: '/build', title: 'Build console', phone: 'more', desktopOnly: false, package: 'v3.0 dispatch', shipped: true },
  { id: 'you', path: '/you', title: 'You', phone: 'tab', desktopOnly: false, package: 'v1.1 auth', shipped: true },
] as const;

/** The four phone tabs, in nav order; the bar adds *More* after them. */
export const PHONE_TABS: readonly NavItem[] = NAV.filter((n) => n.phone === 'tab');
/** What the phone's *More* sheet lists, in nav order. */
export const PHONE_MORE: readonly NavItem[] = NAV.filter((n) => n.phone === 'more');

/** The kinds of object page the shell names in its breadcrumb when the page itself sets no crumbs. */
export type ObjectKind = 'decision' | 'session' | 'weeklyStatus' | 'workPackage' | 'planChange' | 'buildRun' | 'gate' | 'finding' | 'translationReview' | 'document' | 'wireframe' | 'thread' | 'track' | 'story';

/**
 * Which nav item owns a path (IA §1.2: a decision page highlights *Tracks*, a gate page *Plan*, a
 * finding *Build console*) and the object kind for the breadcrumb. First match wins; the order puts
 * the specific paths before their prefixes. The old room paths (`/decisions`, `/sessions`, `/verify*`)
 * are not here: they redirect (REDIRECTS) before the shell asks.
 */
const OWNERS: [RegExp, NavId, ObjectKind | null][] = [
  [/^\/?$/, 'pulse', null],
  [/^\/status\//, 'pulse', 'weeklyStatus'],
  [/^\/decisions\/[^/]+/, 'tracks', 'decision'],
  [/^\/sessions\/[^/]+/, 'tracks', 'session'],
  [/^\/packages\//, 'tracks', 'workPackage'],
  [/^\/tracks\/[^/]+/, 'tracks', 'track'],
  [/^\/tracks\/?$/, 'tracks', null],
  [/^\/plan\/units/, 'tracks', null],
  [/^\/plan\/changes\//, 'plan', 'planChange'],
  [/^\/plan\/gates\/[^/]+/, 'plan', 'gate'],
  [/^\/plan(\/|$)/, 'plan', null],
  [/^\/findings\/[^/]+/, 'build', 'finding'],
  [/^\/build\/runs\//, 'build', 'buildRun'],
  [/^\/build(\/|$)/, 'build', null],
  [/^\/translations\//, 'docs', 'translationReview'],
  [/^\/docs\/.+/, 'docs', 'document'],
  [/^\/docs\/?$/, 'docs', null],
  [/^\/wireframes\/stale\/?$/, 'wireframes', null],
  [/^\/wireframes\/[^/]+/, 'wireframes', 'wireframe'],
  [/^\/wireframes\/?$/, 'wireframes', null],
  [/^\/threads\/[^/]+/, 'workspace', 'thread'],
  [/^\/threads\/?$/, 'workspace', null],
  [/^\/stories\/[^/]+/, 'stories', 'story'],
  [/^\/stories\/?$/, 'stories', null],
  [/^\/you(\/|$)/, 'you', null],
  // Activity (§8 *Activity*, v3.54): the maintainers' page, reached from You › Team.
  [/^\/activity(\/|$)/, 'you', null],
];

export interface PathOwner {
  /** The nav item the path highlights (IA §1.2): an object URL highlights the item that owns its type. */
  nav: NavItem;
  object: ObjectKind | null;
}

export function ownerOf(pathname: string): PathOwner | null {
  const hit = OWNERS.find(([re]) => re.test(pathname));
  if (!hit) return null;
  const nav = NAV.find((n) => n.id === hit[1])!;
  return { nav, object: hit[2] };
}

export interface RedirectRule {
  /** The old path; capture groups feed `to`. */
  from: RegExp;
  /** The new path, and the query it carries — the old one by default, edited where IA §1.2 says so. */
  to: (match: RegExpMatchArray, query: URLSearchParams) => string;
  /** Live rules answer; declared ones wait for the package that moves their room (IA §1.2). */
  active: boolean;
  /** The package that switches the rule on. */
  package: string;
}

const UNIT_ID = /^[a-z0-9][a-z0-9-]{0,80}$/;

/** `programme` is the pinned row of the track list (IA §2.3), never a unit id: `check` refuses a unit named so. */
export const PROGRAMME_UNIT = 'programme';

/**
 * The redirect table of IA §1.2. Each old room path becomes a `301` carrying its query string, so
 * a filtered link in a Slack digest or a saved bookmark stays filtered. Object URLs are not here:
 * they do not move.
 */
export const REDIRECTS: readonly RedirectRule[] = [
  { from: /^\/plan\/units\/?$/, to: () => '/tracks', active: true, package: 'v3.8a flat nav + routes' },
  { from: /^\/plan\/units\/([^/]+)\/?$/, to: (m) => `/tracks/${m[1]}`, active: true, package: 'v3.8a flat nav + routes' },
  {
    from: /^\/decisions\/?$/,
    // A whole-programme link keeps the whole-programme population (`scope=all`, IA §2.3); a link
    // filtered to one track opens that track's tab; `mine=1` alone is the inbox, now on You.
    to: (_m, q) => {
      const unit = q.get('unit');
      const keys = [...q.keys()];
      if (unit && UNIT_ID.test(unit) && unit !== PROGRAMME_UNIT) {
        q.delete('unit');
        return `/tracks/${unit}/decisions`;
      }
      if (q.get('mine') === '1' && keys.every((k) => k === 'mine')) {
        q.delete('mine');
        return '/you#mine';
      }
      q.set('scope', 'all');
      return `/tracks/${PROGRAMME_UNIT}/decisions`;
    },
    active: true,
    package: 'v3.8b tracks decide',
  },
  {
    from: /^\/sessions\/?$/,
    to: (_m, q) => {
      const unit = q.get('unit');
      if (unit && UNIT_ID.test(unit) && unit !== PROGRAMME_UNIT) {
        q.delete('unit');
        return `/tracks/${unit}/sessions`;
      }
      return `/tracks/${PROGRAMME_UNIT}/sessions`;
    },
    active: true,
    package: 'v3.8b tracks decide',
  },
  // The desk (IA §1.5, v3.8c): its home is Plan › Gates (`?mine=1`, the desk's "yours to verify", is You › Assignments);
  // a gate page and an item keep their query (`?mine=1` lists the viewer's items on the gate page).
  {
    from: /^\/verify\/?$/,
    to: (_m, q) => {
      if (q.get('mine') === '1' && [...q.keys()].every((k) => k === 'mine')) {
        q.delete('mine');
        return '/you#assignments';
      }
      return GATES_PATH;
    },
    active: true,
    package: 'v3.8c the desk dispersed',
  },
  { from: /^\/verify\/findings\/([^/]+)\/?$/, to: (m) => `/findings/${m[1]}`, active: true, package: 'v3.8c the desk dispersed' },
  { from: /^\/verify\/([^/]+)\/items\/([^/]+)\/?$/, to: (m) => `/plan/gates/${m[1]}/items/${m[2]}`, active: true, package: 'v3.8c the desk dispersed' },
  { from: /^\/verify\/([^/]+)\/?$/, to: (m) => `/plan/gates/${m[1]}`, active: true, package: 'v3.8c the desk dispersed' },
] as const;

/**
 * The location an old path redirects to — path, query and hash — or null when the path is not an
 * old room path (or its rule is still declared, not live). `search` is the raw query string, with
 * or without its `?`.
 */
export function redirectFor(pathname: string, search = '', options: { includeDeclared?: boolean } = {}): string | null {
  for (const rule of REDIRECTS) {
    if (!rule.active && !options.includeDeclared) continue;
    const match = pathname.match(rule.from);
    if (!match) continue;
    const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const target = rule.to(match, query);
    const [path, hash] = target.split('#');
    const qs = query.toString();
    return `${path}${qs ? `?${qs}` : ''}${hash ? `#${hash}` : ''}`;
  }
  return null;
}

/** The track list and a track's page (IA §2.1); every emitter reads these, none spells the path. */
export const trackPath = (unit: string, tab?: TrackTab | 'sessions'): string => `/tracks/${encodeURIComponent(unit)}${tab ? `/${tab}` : ''}`;
export const TRACKS_PATH = '/tracks';
/**
 * The whole-programme decision list with the room's filters (IA §2.3): what `/decisions?<filters>`
 * was — the Programme row's Decisions tab, scope *all*, the filters appended. Pulse's tiles, the
 * status page's citations and the digest's package links read this, so they land where the 301
 * would have sent them without the round trip.
 */
export const programmeDecisionsPath = (filters: Record<string, string> = {}): string => {
  const q = new URLSearchParams({ scope: 'all', ...filters });
  return `${trackPath(PROGRAMME_UNIT, 'decisions')}?${q.toString()}`;
};

/**
 * The Verification desk's pieces at their homes (IA §1.5, v3.8c); every emitter — notifications, Slack,
 * MR descriptions, the rework prompt, the web — reads these, none spells a path. `/findings/:id` is an
 * object URL from now on; a gate and its items live under Plan › Gates; the findings inbox under the
 * Build console.
 */
export const GATES_PATH = '/plan/gates';
/** The calendar (IA §6.8, v3.10): the Plan's view on real weeks, its own path like the Gates. */
export const CALENDAR_PATH = '/plan/calendar';
export const FINDINGS_INBOX_PATH = '/build/findings';
export const gatePath = (phase: string, options: { mine?: boolean } = {}): string => `${GATES_PATH}/${encodeURIComponent(phase)}${options.mine ? '?mine=1' : ''}`;
export const gateItemPath = (phase: string, itemId: string): string => `${GATES_PATH}/${encodeURIComponent(phase)}/items/${encodeURIComponent(itemId)}`;
/** A finding's page; `query` carries a one-shot instruction (`merge=<sha>`, `confirm=still-broken`) the notifications deep-link with. */
export const findingPath = (id: string, query: Record<string, string> = {}): string => {
  const q = new URLSearchParams(query).toString();
  return `/findings/${encodeURIComponent(id)}${q ? `?${q}` : ''}`;
};
/** The finding ids a merge request's text links (planning-app.md §3.5), in both forms: the one emitted since v3.8c and the desk's, which the open MRs still carry. */
export const findingIdsIn = (text: string): string[] => [...text.matchAll(/\/(?:verify\/)?findings\/([0-9a-f-]{8,36})/gi)].map((m) => m[1]!.toLowerCase());

/**
 * User Stories (IA §3, `v3.9a`): the list and a story's page. `from` is where the page's *Back*
 * returns — the list with its filters, a track's Stories tab, a wireframe page — as `decisionPath`
 * carries it; `filters` is the list's query string (`storyFiltersToSearch`).
 */
export const STORIES_PATH = '/stories';
export const storiesPath = (search = ''): string => `${STORIES_PATH}${search.startsWith('?') || search === '' ? search : `?${search}`}`;
export const storyPath = (id: string, context?: { from?: string | null }): string => {
  const q = new URLSearchParams();
  if (context?.from) q.set('from', context.from);
  const qs = q.toString();
  return `${STORIES_PATH}/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`;
};
/** A wireframe page, in one of its deep-link states (`?step=1`) when given. */
export const wireframePath = (file: string, state = ''): string => `/wireframes/${encodeURIComponent(file.split('/').pop() ?? file)}${state}`;

/** The tabs of a track page, in order (IA §2.1); the ones whose data lands later render their empty state naming the release. */
export const TRACK_TABS = ['overview', 'decisions', 'work', 'stories', 'wireframes', 'documents', 'threads', 'history'] as const;
export type TrackTab = (typeof TRACK_TABS)[number];
/** The Programme row's tabs (IA §2.3): a projection of the whole plan, so no Work, Stories, Wireframes or Threads of its own. */
export const PROGRAMME_TABS = ['overview', 'decisions', 'sessions', 'documents'] as const;
export type ProgrammeTab = (typeof PROGRAMME_TABS)[number];
/** The `scope` of the Programme row's Decisions tab (IA §2.3): `*` decisions only, the ones naming several tracks, or every decision. */
export const PROGRAMME_SCOPES = ['programme', 'shared', 'all'] as const;
export type ProgrammeScope = (typeof PROGRAMME_SCOPES)[number];

/**
 * The decision page's context (IA §2.2–§2.3): the link carries `?track=<id>` when it comes from a
 * track tab, a document panel or a session row; `from` is where *Back* returns — an in-app path
 * (`/tracks/hiring/decisions?status=open`) or `doc:<path>` for the reader.
 */
export const decisionPath = (id: string, context?: { track?: string | null; from?: string | null }): string => {
  const q = new URLSearchParams();
  if (context?.track) q.set('track', context.track);
  if (context?.from) q.set('from', context.from);
  const qs = q.toString();
  return `/decisions/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`;
};

/**
 * The track a bare decision link opens under (IA §2.3): the track whose owner is the effective
 * owner, else the first named track with a stored owner, else the first track in `blocks`; the
 * Programme row when the decision blocks `*` or names no track at all.
 */
export function contextTrackOf(d: { blocks: string[]; owner: { id: string } | null; tracks: { id: string; owner: { id: string } | null }[] }): string {
  if (d.tracks.length === 0) return PROGRAMME_UNIT;
  if (d.owner) {
    const owning = d.tracks.find((t) => t.owner?.id === d.owner!.id);
    if (owning) return owning.id;
  }
  const owned = d.tracks.find((t) => t.owner !== null);
  if (owned) return owned.id;
  const named = d.blocks.find((b) => d.tracks.some((t) => t.id === b));
  return named ?? d.tracks[0]!.id;
}
